import { updateDoiInventoryPolicyJson } from 'pages/master-data/general-configuration/general/importer-exporter-doi-inventory-policy/importer-exporter-doi-inventory-policy.service';
import intl from 'react-intl-universal';
import type { UploadDoiInventoryPolicy } from './doi-inventory-policy.types';

export interface ProcessingResult {
  success: boolean;
  item: UploadDoiInventoryPolicy;
  index: number;
  message: string;
  type: number;
}

export interface ProcessingSummary {
  successCount: number;
  errorCount: number;
  results: ProcessingResult[];
}

interface DuplicateGroup {
  material: string;
  plant: string;
  lineNumbers: number[];
}

export class DoiInventoryPolicyProcessor {
  private readonly languageCode: string;

  constructor(
    private readonly setProcessingPercentual: React.Dispatch<React.SetStateAction<number>>
  ) {
    const { getLanguageCode } = require('locales');
    this.languageCode = getLanguageCode();
  }

  private getDuplicateMaterialMessage(material: string, lines: string, languageCode: string): string {
    if (languageCode === 'pt') {
      return `Material ${material} possui informações repetidas nas linhas ${lines}`;
    } else if (languageCode === 'es') {
      return `Material ${material} contiene información repetida en las líneas ${lines}`;
    }
    return `Material ${material} has repeated information in lines ${lines}`;
  }

  private validateDuplicateItems(items: UploadDoiInventoryPolicy[]): DuplicateGroup[] {
    const duplicateMap = new Map<string, DuplicateGroup>();
    const { plantField } = this.getFieldNamesByLanguage();

    items.forEach((item, index) => {
      const key = `${item.Material}_${item[plantField]}`;
      if (!duplicateMap.has(key)) {
        duplicateMap.set(key, {
          material: item.Material ?? '',
          plant: item[plantField] ?? '',
          lineNumbers: [index + 1]
        });
      } else {
        duplicateMap.get(key)?.lineNumbers.push(index + 1);
      }
    });

    return Array.from(duplicateMap.values())
      .filter(group => group.lineNumbers.length > 1);
  }

  async processItems(fileData: UploadDoiInventoryPolicy[]): Promise<ProcessingSummary> {
    const summary: ProcessingSummary = {
      successCount: 0,
      errorCount: 0,
      results: []
    };

    const duplicateGroups = this.validateDuplicateItems(fileData);
    if (duplicateGroups.length > 0) {
      this.updateProgress(fileData.length, fileData.length);

      const messages = duplicateGroups.map(group => {
        const lines = group.lineNumbers.map(x => x + 1).join(', ');
        return {
          firstLineDuplicated: group.lineNumbers[0],
          message: this.getDuplicateMaterialMessage(group.material, lines, this.languageCode)
        };
      });

      return {
        successCount: 0,
        errorCount: fileData.length,
        results: messages.map((x, i) => ({
          success: false,
          item: fileData[x.firstLineDuplicated],
          index: i,
          message: x.message,
          type: 0
        }))
      };
    }

    for (let i = 0; i < fileData.length; i++) {
      const result = await this.processSingleItem(fileData[i], i);

      if (result.success) {
        summary.successCount++;
      } else {
        summary.errorCount++;
      }

      summary.results.push(result);
      this.updateProgress(i + 1, fileData.length);
    }

    return summary;
  }

  private async processSingleItem(item: UploadDoiInventoryPolicy, index: number): Promise<ProcessingResult> {
    try {
      const itemData = this.convertItemToBackendFormat(item);
      this.validateItemRequiredFields(itemData);

      const response = await updateDoiInventoryPolicyJson(itemData);

      if (response.AllSuccess) {
        return this.createSuccessResult(item, index);
      } else {
        return this.createErrorResultFromResponse(item, index, response);
      }

    } catch (error: any) {
      return this.handleItemError(error, item, index);
    }
  }

  private handleItemError(error: any, item: UploadDoiInventoryPolicy, index: number): ProcessingResult {
    console.error(`Erro ao processar item ${index + 1}:`, error);

    const errorMessage = this.formatErrorMessage(error);
    const message = this.createResultMessage(index, item, errorMessage);

    return {
      success: false,
      item,
      index,
      message,
      type: 0
    };
  }

  private updateProgress(current: number, total: number): void {
    const progress = Math.round((current / total) * 100);
    this.setProcessingPercentual(progress);
  }

  buildResponse(summary: ProcessingSummary): any[] {
    const summaryMessage = this.createSummaryMessage(summary.successCount);

    const finalResults = [
      {
        Type: summary.successCount > 0 ? 2 : 0,
        Message: summaryMessage
      },
      ...summary.results.map(result => ({
        Type: result.type,
        Message: result.message
      }))
    ];

    return [{
      AllSuccess: summary.errorCount === 0 && summary.successCount > 0,
      Results: finalResults
    }];
  }

  private parseIntValue(value: any): number {
    if (value === undefined || value === null || value.toString().trim() === '') return 0;
    const parsed = parseInt(value.toString().replace(',', '.'), 10);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  private parseFloatValue(value: any): number {
    if (value === undefined || value === null || value.toString().trim() === '') return 0;
    const parsed = parseFloat(value.toString().replace(',', '.'));
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  private convertItemToBackendFormat(item: UploadDoiInventoryPolicy): any {
    const { plantField, targetField, minField, maxField, floorField } = this.getFieldNamesByLanguage();

    return {
      material: item.Material,
      plant: item[plantField] ?? '',
      targetDoi: this.parseIntValue(item[targetField]),
      minDoi: this.parseIntValue(item[minField]),
      maxDoi: this.parseIntValue(item[maxField]),
      doiFloor: this.parseFloatValue(item[floorField])
    };
  }

  private getPlantLabel(languageCode: string): string {
    if (languageCode === 'pt') return 'Centro';
    if (languageCode === 'es') return 'Centro';
    return 'Unit';
  }

  private getErrorPrefix(languageCode: string): string {
    if (languageCode === 'pt') return 'Campos obrigatórios faltando';
    if (languageCode === 'es') return 'Faltan campos obligatorios';
    return 'Missing required fields';
  }

  private validateItemRequiredFields(itemData: any): void {
    const validations = [
      { field: 'material', value: itemData.material, label: () => 'Material' },
      { field: 'plant', value: itemData.plant, label: () => this.getPlantLabel(this.languageCode) }
    ];

    const missingFields = validations
      .filter(validation => !validation.value)
      .map(validation => validation.label());

    if (missingFields.length > 0) {
      const prefix = this.getErrorPrefix(this.languageCode);
      throw new Error(`${prefix}: ${missingFields.join(', ')}`);
    }
  }

  private getDefaultErrorMessage(languageCode: string): string {
    if (languageCode === 'pt') return 'Erro ao importar';
    if (languageCode === 'es') return 'Error al importar';
    return 'Import error';
  }

  private formatErrorMessage(error: any): string {
    if (error.response) {
      let errorMessage = `Request failed with status code ${error.response.status}`;
      if (error.response.data?.message) {
        errorMessage += ` - ${error.response.data.message}`;
      }
      return errorMessage;
    }

    return error.message || this.getDefaultErrorMessage(this.languageCode);
  }

  private createResultMessage(index: number, item: UploadDoiInventoryPolicy, message: string): string {
    const identifier = this.getItemIdentifier(item);
    return `Item ${index + 1}: ${identifier} - ${message}`;
  }

  private getSuccessMessage(languageCode: string): string {
    if (languageCode === 'pt') return 'Importado com sucesso';
    if (languageCode === 'es') return 'Importado con éxito';
    return 'Successfully imported';
  }

  private createSuccessResult(item: UploadDoiInventoryPolicy, index: number): ProcessingResult {
    const successMessage = this.getSuccessMessage(this.languageCode);
    const message = this.createResultMessage(index, item, successMessage);

    return {
      success: true,
      item,
      index,
      message,
      type: 2
    };
  }

  private createErrorResultFromResponse(item: UploadDoiInventoryPolicy, index: number, response: any): ProcessingResult {
    const errorMessages = response.Results.map((result: any) => result.Message).join('; ');
    const message = this.createResultMessage(index, item, errorMessages);

    return {
      success: false,
      item,
      index,
      message,
      type: response.Results[0]?.Type || 0
    };
  }

  private createSummaryMessage(successCount: number): string {
    return successCount > 0
      ? `${intl.get('master-data.general-configuration.totalSuccessMessage')} ${successCount}`
      : intl.get('master-data.general-configuration.noRecordsWereImported');
  }

  private getItemIdentifier(item: UploadDoiInventoryPolicy): string {
    return item.Material ?? 'Item sem código';
  }

  private getFieldNamesByLanguage() {
    let plantField = 'Unit';
    let targetField = 'Target DOI';
    let minField = 'Min DOI';
    let maxField = 'Max DOI';
    let floorField = 'DOI Floor';

    if (this.languageCode === 'pt') {
      plantField = 'Centro';
      targetField = 'DOI Alvo';
      minField = 'DOI Mínimo';
      maxField = 'DOI Máximo';
      floorField = 'Piso de DOI';
    } else if (this.languageCode === 'es') {
      plantField = 'Centro';
      targetField = 'DOI Objetivo';
      minField = 'DOI Mínimo';
      maxField = 'DOI Máximo';
      floorField = 'Piso de DOI';
    }

    return { plantField, targetField, minField, maxField, floorField };
  }
}
